import React, { useCallback, useEffect, useState } from 'react';

/**
 * The record of what Category Review has decided — searchable, paginated,
 * downloadable.
 *
 * Shows the chosen category next to what the catalog still holds, because the two
 * differ on purpose: a review records the decision without touching
 * `product_category`, so nothing takes effect until the decisions are applied.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

const C = {
  maroon: '#8B1D3C',
  bg: '#FFFFFF',
  bgLight: '#F9FAFB',
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
};

const PER_PAGE = 50;

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Times are stored UTC; reviewers read them in local time.
const localTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (/[Zz+]/.test(iso) ? '' : 'Z'));
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export default function CategoryReviewed({ onBack }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_BASE}/api/category-review/reviewed`
        + `?page=${page}&per_page=${PER_PAGE}`
        + (query ? `&search=${encodeURIComponent(query)}` : '');
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Could not load the list (${res.status})`);
      const data = await res.json();
      setRows(data.designs || []);
      setTotal(data.total || 0);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load the list');
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => { load(); }, [load]);

  // The endpoint needs an Authorization header, so a plain link cannot fetch it —
  // pull the file as a blob and hand it to the browser.
  const download = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/api/category-review/export`,
        { headers: authHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `category_review_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setError('');
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}>Category Records</h1>
          <p style={S.subtitle}>
            Every design whose <strong style={{ color: C.textPrimary }}>Category</strong> a
            person has decided
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={onBack} style={S.secondaryBtn}>← Back to queue</button>
          <button onClick={download} disabled={downloading || !total}
                  style={{ ...S.primaryBtn, opacity: (downloading || !total) ? 0.5 : 1 }}>
            {downloading ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      </header>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.notice}>
        These are recorded decisions. The catalog's Category field is unchanged
        until they are applied, which is why the two columns differ.
      </div>

      <div style={S.toolbar}>
        <form onSubmit={submitSearch} style={{ display: 'flex', gap: 8 }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search design number…" style={S.input} />
          <button type="submit" style={S.secondaryBtn}>Search</button>
          {query && (
            <button type="button" style={S.secondaryBtn}
                    onClick={() => { setSearch(''); setQuery(''); setPage(1); }}>
              Clear
            </button>
          )}
        </form>
        <div style={S.count}>
          <strong style={{ color: C.textPrimary }}>{total}</strong> recorded
        </div>
      </div>

      {loading ? (
        <div style={S.empty}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={S.empty}>
          {query ? `No design matches “${query}”.` : 'Nothing reviewed yet.'}
        </div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {['', 'Design', 'Item', 'Chosen Category', 'Still in catalog',
                  'By', 'When'].map((h) => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.designNumber} style={S.tr}>
                  <td style={S.td}>
                    {r.imageUrl
                      ? <img src={r.imageUrl} alt="" style={S.thumb} />
                      : <div style={{ ...S.thumb, background: C.bgLight }} />}
                  </td>
                  <td style={{ ...S.td, fontWeight: 700 }}>{r.designNumber}</td>
                  <td style={S.td}>{r.itemName || '—'}</td>
                  <td style={S.td}>
                    <span style={S.pill}>{r.category}</span>
                  </td>
                  <td style={{ ...S.td, color: C.textLight }}>
                    {r.catalogCategory || '—'}
                  </td>
                  <td style={{ ...S.td, color: C.textSecondary }}>{r.reviewedBy}</td>
                  <td style={{ ...S.td, color: C.textLight, whiteSpace: 'nowrap' }}>
                    {localTime(r.reviewedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={S.pager}>
          <button style={S.secondaryBtn} disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ← Previous
          </button>
          <span style={{ color: C.textSecondary, fontSize: 13 }}>
            Page {page} of {totalPages}
          </span>
          <button style={S.secondaryBtn} disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

const S = {
  page: { padding: '28px 32px', maxWidth: 1180, margin: '0 auto' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    marginBottom: 22, paddingBottom: 18, borderBottom: `1px solid ${C.border}`,
    gap: 24, flexWrap: 'wrap',
  },
  title: {
    fontSize: 30, fontWeight: 800, color: C.textPrimary, margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: { fontSize: 14, color: C.textSecondary, margin: '6px 0 0' },
  notice: {
    background: C.bgLight, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '9px 13px', marginBottom: 14, fontSize: 13, color: C.textSecondary,
  },
  toolbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 14, gap: 16, flexWrap: 'wrap',
  },
  input: {
    padding: '9px 13px', borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 14, minWidth: 230, color: C.textPrimary,
  },
  count: { fontSize: 13, color: C.textSecondary },
  tableWrap: {
    overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 12,
    background: C.bg,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: {
    textAlign: 'left', padding: '11px 14px', fontSize: 11,
    textTransform: 'uppercase', letterSpacing: '0.07em', color: C.textLight,
    borderBottom: `1px solid ${C.border}`, background: C.bgLight,
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: `1px solid ${C.border}` },
  td: { padding: '10px 14px', color: C.textPrimary, verticalAlign: 'middle' },
  thumb: {
    width: 40, height: 40, objectFit: 'cover', borderRadius: 6,
    border: `1px solid ${C.border}`, display: 'block',
  },
  pill: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 999,
    background: 'rgba(139,29,60,0.07)', color: C.maroon, fontSize: 12.5,
    fontWeight: 600, marginRight: 6,
  },
  empty: {
    padding: '46px 0', textAlign: 'center', color: C.textLight,
    border: `1px dashed ${C.border}`, borderRadius: 12,
  },
  pager: {
    display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center',
    marginTop: 18,
  },
  error: {
    background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5',
    borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14,
  },
  secondaryBtn: {
    padding: '9px 16px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${C.border}`, background: C.bg, color: C.textSecondary,
    fontSize: 14, fontWeight: 600,
  },
  primaryBtn: {
    padding: '9px 18px', borderRadius: 8, cursor: 'pointer', border: 'none',
    background: C.maroon, color: '#fff', fontSize: 14, fontWeight: 700,
  },
};
